import { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Iconify from 'src/components/Iconify';
import useAuth from 'src/hooks/useAuth';
import { MKT_ANALYTICS_DESIGN as d } from './MktAnalytics.design';
import PeriodFilter from './components/PeriodFilter';
import OverviewTab from './OverviewTab';
import InstagramTab from './InstagramTab';
import FacebookTab from './FacebookTab';
import GoogleAdsTab from './GoogleAdsTab';
import {
    fetchOverview,
    fetchInstagramInsights,
    fetchFacebookInsights,
    fetchGoogleAdsInsights,
    setActiveTab
} from 'src/redux/slices/mktAnalytics';

const TABS = [
    { id: 'overview', label: 'Visão Geral', icon: 'eva:grid-fill' },
    { id: 'instagram', label: 'Instagram', icon: 'mdi:instagram' },
    { id: 'facebook', label: 'Facebook', icon: 'mdi:facebook' },
    { id: 'google_ads', label: 'Google Ads', icon: 'mdi:google-ads' }
];

function getDateRange(period) {
    const end = new Date();
    const start = new Date();

    switch (period) {
        case 'last-7-days': start.setDate(end.getDate() - 7); break;
        case 'last-14-days': start.setDate(end.getDate() - 14); break;
        case 'last-30-days': start.setDate(end.getDate() - 30); break;
        case 'last-90-days': start.setDate(end.getDate() - 90); break;
        default: start.setDate(end.getDate() - 7);
    }

    return {
        start: start.toISOString().split('T')[0],
        end: end.toISOString().split('T')[0]
    };
}

export default function MktAnalytics() {
    const dispatch = useDispatch();
    const { user } = useAuth();
    const { insights, activeTab, isLoading } = useSelector(state => state.mktAnalytics);

    const [period, setPeriod] = useState('last-7-days');
    const [customStart, setCustomStart] = useState('');
    const [customEnd, setCustomEnd] = useState('');

    const dateRange = useMemo(() => {
        if (period === 'custom' && customStart && customEnd) {
            return { start: customStart, end: customEnd };
        }
        return getDateRange(period);
    }, [period, customStart, customEnd]);

    const querys = useMemo(() => {
        return `?start_date=${dateRange.start}&end_date=${dateRange.end}`;
    }, [dateRange]);

    const loadData = useCallback(() => {
        const clienteId = user?.clienteId;
        if (!clienteId) return;

        switch (activeTab) {
            case 'overview':
                dispatch(fetchOverview(clienteId, querys));
                break;
            case 'instagram':
                dispatch(fetchInstagramInsights(clienteId, querys));
                break;
            case 'facebook':
                dispatch(fetchFacebookInsights(clienteId, querys));
                break;
            case 'google_ads':
                dispatch(fetchGoogleAdsInsights(clienteId, querys));
                break;
        }
    }, [dispatch, user?.clienteId, activeTab, querys]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const handleTabChange = (tabId) => {
        dispatch(setActiveTab(tabId));
    };

    return (
        <div className={d.page.container}>
            {/* Header */}
            <div className={d.page.header.wrapper}>
                <div className={d.page.header.left}>
                    <div className={d.page.header.iconBox}>
                        <Iconify icon="eva:activity-fill" width={28} className={d.page.header.icon} />
                    </div>
                    <div>
                        <h1 className={d.page.header.title}>Social Analytics</h1>
                        <p className={d.page.header.subtitle}>Acompanhe o desempenho das suas redes sociais</p>
                    </div>
                </div>
            </div>

            {/* Period Filter */}
            <PeriodFilter
                period={period}
                onPeriodChange={setPeriod}
                startDate={customStart}
                endDate={customEnd}
                onStartDateChange={setCustomStart}
                onEndDateChange={setCustomEnd}
            />

            {/* Tabs */}
            <div className={d.tabs.container}>
                {TABS.map(tab => (
                    <button
                        key={tab.id}
                        className={activeTab === tab.id ? d.tabs.tabActive : d.tabs.tab}
                        onClick={() => handleTabChange(tab.id)}
                    >
                        <span className="flex items-center gap-1.5">
                            <Iconify icon={tab.icon} width={16} />
                            {tab.label}
                        </span>
                    </button>
                ))}
            </div>

            {/* Loading */}
            {isLoading && (
                <div className="flex items-center justify-center py-8">
                    <div className="w-8 h-8 border-2 border-violet-200 border-t-violet-600 rounded-full animate-spin" />
                </div>
            )}

            {/* Tab Content */}
            {!isLoading && activeTab === 'overview' && <OverviewTab data={insights.overview} />}
            {!isLoading && activeTab === 'instagram' && <InstagramTab data={insights.instagram} />}
            {!isLoading && activeTab === 'facebook' && <FacebookTab data={insights.facebook} />}
            {!isLoading && activeTab === 'google_ads' && <GoogleAdsTab data={insights.googleAds} />}
        </div>
    );
}
